import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { AUDIENCES } from '@/data/audiences';

interface Props {
  /** `band` lifts the cards over the hero. `grid` renders them inline. */
  variant?: 'band' | 'grid';
}

/**
 * FOUR-AUDIENCE SELECTOR — the first decision on the page.
 *
 * The home page previously spoke almost entirely to homeowners, and the only
 * "pick your lane" block sat ~5 screens down with three options. This puts all
 * FOUR audiences (homeowner, commercial property owner, general contractor,
 * subcontractor) directly under the headline, each with its own icon, its own
 * one-line promise and its own destination.
 *
 * Homeowner visibility is NOT reduced: the homeowner card is still first, still
 * full size, and still the widest colour block on mobile.
 *
 * Data comes from src/data/audiences.ts so the register screen, the header nav
 * and the footer cannot disagree with this block.
 */
const AudienceSelector: React.FC<Props> = ({ variant = 'band' }) => {
  const { t, path } = useI18n();

  const band = variant === 'band';

  return (
    <section
      className={band ? 'relative z-10 -mt-10 bg-transparent sm:-mt-14' : 'bg-brand-canvas py-14'}
      aria-labelledby="audience-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={band ? 'rounded-3xl border border-slate-200 bg-white p-5 shadow-card sm:p-7' : ''}>
          <div className={band ? 'mb-5 flex flex-wrap items-end justify-between gap-2' : 'text-center'}>
            <div>
              <h2
                id="audience-heading"
                className={
                  band
                    ? 'text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl'
                    : 'text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl'
                }
              >
                {t('aud.selectorTitle')}
              </h2>
              <p className={band ? 'mt-1 text-sm text-slate-500' : 'mx-auto mt-3 max-w-2xl text-base text-slate-500'}>
                {t('aud.selectorSub')}
              </p>
            </div>
          </div>

          {/* 2x2 on phones (never a single cramped column), 4 across on desktop. */}
          <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {AUDIENCES.map((a) => (
              <li key={a.key} className="h-full">
                <Link
                  to={path(a.to)}
                  className={`group flex h-full flex-col rounded-2xl border p-4 text-left transition-all duration-200 hover:-translate-y-1 hover:shadow-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 sm:p-5 ${
                    a.featured
                      ? 'border-orange-300 bg-orange-50/60 ring-1 ring-orange-200'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <span
                    className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ring-1 transition-transform duration-200 group-hover:scale-110 ${a.accent}`}
                  >
                    <a.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="mt-3 text-sm font-extrabold leading-snug text-slate-900 sm:text-base">
                    {t(a.titleKey)}
                  </span>
                  <span className="mt-1 flex-1 text-xs leading-relaxed text-slate-500 sm:text-sm">
                    {t(a.descKey)}
                  </span>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange sm:text-sm">
                    {t(a.ctaKey)}
                    <ArrowRight
                      className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 sm:h-4 sm:w-4"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Secondary row: the deeper landing page for each audience. Keeps the
              cards to ONE primary action while still exposing the SEO pages. */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold text-slate-500">
            <span className="text-slate-400">{t('aud.learnMore')}</span>
            {AUDIENCES.map((a) => (
              <Link key={a.key} to={path(a.learn)} className="underline-offset-2 hover:text-slate-900 hover:underline">
                {t(a.titleKey)}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceSelector;
